import React, { ChangeEvent, FC, useState } from 'react';
import { useThemeContext } from '../../../../theme/ThemeContextProvider';
import SearchContacts from '../NewPersonalChat/SearchContacts';
import { Box } from '@mui/material';
import CustomButton from '../../../../components/MUIComponents/CustomButton';
import { ButtonTypeEnum } from '../../../../enums/button-type-enum';
import {
  IGroupChat,
  useAddGroupChatMembersMutation,
  useGetAvailableFriendsQuery,
} from '../../../../store/apiSlice/chat/groupApi';
import {
  getSelectedChat,
  setChatsLoaded,
  setSelectedChat,
} from '../../../../store/apiSlice/chat/chatSlice';
import { useDispatch, useSelector } from 'react-redux';
import { selectAuthData } from '../../../../store/selectors/authSelector';
import { IFriend } from '../../../../interfaces/friend.interface';
import ChatSuggestionsList from '../ChatSuggestionsList/ChatSuggestionsList';
import { mapFriendToUser } from '../ChatSuggestionsList/suggestionsUtils';
import useDebounce from '../../../../hooks/useDebounce';

interface InvitationFormProps {
  onClose: () => void;
}

const InvitationForm: FC<InvitationFormProps> = ({ onClose }) => {
  const { theme } = useThemeContext();
  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedFriends, setSelectedFriends] = useState<IFriend[]>([]);
  const [addGroupChatMembers] = useAddGroupChatMembersMutation();
  const authData = useSelector(selectAuthData);
  const selectedChat = useSelector(getSelectedChat)! as IGroupChat;
  const dispatch = useDispatch();

  const debouncedSearch = useDebounce(searchValue, 500);
  const { data: availableFriends } = useGetAvailableFriendsQuery({
    searchValue: debouncedSearch,
    groupId: selectedChat.id,
  });

  const handleSelectFriend = (friend: IFriend) => {
    setSelectedFriends(prev => {
      const isSelected = prev.some(user => user.userId === friend.userId);
      return isSelected
        ? prev.filter(user => user.userId !== friend.userId)
        : [...prev, friend];
    });
  };

  const handleInvite = () => {
    addGroupChatMembers({
      groupId: selectedChat.id,
      addMemberDto: selectedFriends.map(mapFriendToUser),
      userId: authData.user.id,
    })
      .unwrap()
      .then(group => {
        dispatch(setSelectedChat(group));
        dispatch(setChatsLoaded());
      })
      .catch(console.log);
    onClose();
  };

  return (
    <>
      <SearchContacts
        onSearch={(e: ChangeEvent<HTMLInputElement>) =>
          setSearchValue(e.target.value)
        }
      />

      <ChatSuggestionsList
        friends={availableFriends?.friends ?? []}
        onClick={handleSelectFriend}
        selectedFriends={selectedFriends}
      />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '1rem',
          borderTop: `1px solid ${theme.palette.background.dark}`,
        }}
      >
        <CustomButton
          variantType={ButtonTypeEnum.SECONDARY}
          onClick={onClose}
          buttonWidth={'13.75rem'}
        >
          Cancel
        </CustomButton>
        <CustomButton
          variantType={ButtonTypeEnum.PRIMARY}
          onClick={handleInvite}
          buttonWidth={'13.75rem'}
          disabled={selectedFriends.length === 0}
        >
          Invite
        </CustomButton>
      </Box>
    </>
  );
};

export default InvitationForm;
